import React from 'react';

const LoadingOverlay = ({ 
  isLoading = false, 
  message = 'Loading...', 
  fullScreen = false,
  className = '',
  spinnerSize = 'md',
  backdrop = true
}) => {
  if (!isLoading) return null;

  const spinnerSizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const overlayClasses = fullScreen 
    ? 'fixed inset-0 z-overlay' :'absolute inset-0';

  return (
    <div 
      className={`
        ${overlayClasses}
        flex items-center justify-center
        ${backdrop ? 'bg-background/80 modal-backdrop' : ''}
        ${className}
      `}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <div className="flex flex-col items-center gap-3">
        {/* Spinner */}
        <div className="relative">
          <div 
            className={`
              ${spinnerSizes[spinnerSize]}
              border-2 border-surface-200 border-t-primary
              rounded-full animate-spin
            `}
            aria-hidden="true"
          />
        </div>
        
        {/* Loading message */}
        {message && (
          <div className="text-sm font-medium text-muted-foreground text-center">
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

// Skeleton loader component for form fields
export const SkeletonLoader = ({ 
  lines = 1, 
  className = '',
  height = 'h-4',
  animated = true 
}) => {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`
            ${height} bg-muted-200 rounded
            ${animated ? 'animate-pulse' : ''}
            ${index === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}
          `}
        />
      ))}
    </div>
  );
};

// Button loading state component
export const ButtonSpinner = ({ 
  size = 'sm', 
  className = '' 
}) => {
  const spinnerSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div 
      className={`
        ${spinnerSizes[size]}
        border-2 border-current border-t-transparent
        rounded-full animate-spin
        ${className}
      `}
      aria-hidden="true"
    />
  );
};

// Progress bar component
export const ProgressBar = ({ 
  progress = 0, 
  className = '',
  showPercentage = false,
  animated = true 
}) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  return (
    <div className={`w-full ${className}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-muted-foreground">
          Processing...
        </span>
        {showPercentage && (
          <span className="text-sm font-medium text-muted-foreground">
            {Math.round(clampedProgress)}%
          </span>
        )}
      </div>
      <div className="w-full bg-muted-200 rounded-full h-2">
        <div
          className={`
            bg-primary h-2 rounded-full transition-all duration-300 ease-out
            ${animated ? 'animate-pulse' : ''}
          `}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

export default LoadingOverlay;

